import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import {
  STAMMDATEN_NAV_GROUPS,
  getStammdatenNavByKey,
  type NavItem,
  type StammdatenNavGroup,
} from "@/lib/nav";
import { usePageHeader } from "@/layout/usePageHeader";
import { NavIcon } from "@/navigation/NavIcon";
import { StammdatenNavTile } from "@/pages/stammdaten/StammdatenNavTile";
import { StammdatenSearch } from "@/pages/stammdaten/StammdatenSearch";
import {
  STAMMDATEN_QUICK_TASK_IDS,
  getStammdatenSearchEntry,
} from "@/pages/stammdaten/stammdatenSearchIndex";

interface GroupWithItems extends StammdatenNavGroup {
  items: NavItem[];
}

function StammdatenGroupSection({ group, index }: { group: GroupWithItems; index: number }) {
  if (group.items.length === 0) return null;

  return (
    <section aria-labelledby={`stammdaten-group-${group.key}`} className="space-y-3">
      <div className="flex items-start gap-3">
        <span
          className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-fill-tertiary text-xs font-semibold tabular-nums text-label-secondary"
          aria-hidden
        >
          {index + 1}
        </span>
        <div className="min-w-0">
          <h2 id={`stammdaten-group-${group.key}`} className="text-base font-semibold text-label">
            {group.label}
          </h2>
          <p className="text-sm text-label-secondary">{group.description}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {group.items.map((item) => (
          <StammdatenNavTile key={item.key} item={item} />
        ))}
      </div>
    </section>
  );
}

function QuickTasks() {
  const tasks = useMemo(
    () =>
      STAMMDATEN_QUICK_TASK_IDS.map((id) => getStammdatenSearchEntry(id)).filter(
        (entry): entry is NonNullable<typeof entry> => entry != null,
      ),
    [],
  );

  if (tasks.length === 0) return null;

  return (
    <section aria-labelledby="stammdaten-quick-tasks" className="space-y-3">
      <div className="flex items-center gap-2">
        <Sparkles size={16} aria-hidden className="text-accent" />
        <h2 id="stammdaten-quick-tasks" className="text-sm font-semibold text-label">
          Häufige Aufgaben
        </h2>
      </div>
      <ul className="flex flex-wrap gap-2">
        {tasks.map((task) => {
          const item = getStammdatenNavByKey(task.itemKey);
          return (
            <li key={task.id}>
              <Link
                to={task.href}
                title={task.hint}
                className="group inline-flex items-center gap-2 rounded-full border border-separator bg-card py-1.5 pl-2.5 pr-3 text-sm text-label transition hover:border-accent/40 hover:bg-accent-soft hover:text-accent"
              >
                {item?.icon ? (
                  <NavIcon name={item.icon} size={15} className="text-label-tertiary group-hover:text-accent" />
                ) : null}
                <span>{task.label}</span>
                <ArrowRight
                  size={14}
                  aria-hidden
                  className="-ml-0.5 text-label-tertiary transition group-hover:translate-x-0.5 group-hover:text-accent"
                />
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function StammdatenOverviewPage() {
  const { setPageMeta } = usePageHeader();

  const groups = useMemo<GroupWithItems[]>(
    () =>
      STAMMDATEN_NAV_GROUPS.map((group) => ({
        ...group,
        items: group.itemKeys
          .map((key) => getStammdatenNavByKey(key))
          .filter((item): item is NavItem => item != null),
      })).filter((group) => group.items.length > 0),
    [],
  );

  useEffect(() => {
    setPageMeta({
      title: "Stammdaten",
      subtitle: "Platz, Preise, Regeln und Geräte einrichten",
    });
    return () => setPageMeta(null);
  }, [setPageMeta]);

  return (
    <div className="space-y-8">
      <section className="rounded-2xl border border-separator bg-card p-5 shadow-ios sm:p-6">
        <div className="max-w-3xl space-y-1.5">
          <h1 className="text-2xl font-semibold tracking-tight text-label">Stammdaten</h1>
          <p className="text-sm leading-relaxed text-label-secondary">
            Hier richten Sie alles ein, was im Alltag gilt: Stellplätze, Preise, Regeln für
            Buchungen und Rechnungen sowie Website, Tablet und Bildschirme für Ihre Gäste.{" "}
            <span className="hidden sm:inline">
              Tippen Sie einfach ein, was Sie einstellen möchten – oder wählen Sie unten einen
              Bereich.
            </span>
          </p>
        </div>
        <div className="mt-4">
          <StammdatenSearch />
        </div>
      </section>

      <QuickTasks />

      <div className="space-y-8">
        {groups.map((group, index) => (
          <StammdatenGroupSection key={group.key} group={group} index={index} />
        ))}
      </div>
    </div>
  );
}
